import _extends from "@babel/runtime/helpers/esm/extends";
import _classPrivateFieldLooseBase from "@babel/runtime/helpers/esm/classPrivateFieldLooseBase";
import _classPrivateFieldLooseKey from "@babel/runtime/helpers/esm/classPrivateFieldLooseKey";
// Classes
import Metrics from "./Metrics.js"; // Types
// Constants
export const HISTOGRAM_TYPE = 'metrics.histogram';
export const HISTOGRAM_VERSION = '1.0.0';
var _buckets = /*#__PURE__*/_classPrivateFieldLooseKey("buckets");
var _count = /*#__PURE__*/_classPrivateFieldLooseKey("count");
var _sum = /*#__PURE__*/_classPrivateFieldLooseKey("sum");
/**
 * A histogram samples observations (usually things like request durations or response sizes) and counts them in configurable buckets.
 * It also provides a sum of all observed values.
 * See https://deezer.jira.com/l/c/1Y101sJ2 for more information.
 * See https://github.deezerdev.com/gpelat/notebooks/blob/master/2020-07-16_remote_metrics_schema.md#histogram-schemas-definition for specs.
 */
export default class HistogramMetrics extends Metrics {
  constructor(options) {
    super(options);
    Object.defineProperty(this, _buckets, {
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, _count, {
      writable: true,
      value: 0
    });
    Object.defineProperty(this, _sum, {
      writable: true,
      value: 0
    });
    const {
      buckets
    } = options;
    if (!buckets.length) {
      throw new Error('Buckets values are mandatory');
    }
    _classPrivateFieldLooseBase(this, _buckets)[_buckets] = [...new Set(buckets)].sort((a, b) => a - b).map(limit => new HistogramBucket(limit));
  }

  /**
   * Will add a value to the histogram.
   * Each bucket that has a higher 'less_eq' value than the given value will be increased by 1.
   * If your buckets are `[1, 10, Infinity]` and the value is `2`, then your buckets will end up in this configuration:
   * [
   *   {less_eq: 1, count: 0},
   *   {less_eq: 10, count: 1}, // +1
   *   {less_eq: Infinity, count: 1}, // +1
   * ]
   * @param value The value to add to the Histogram
   */
  add(value) {
    const buckets = _classPrivateFieldLooseBase(this, _buckets)[_buckets].filter(bucket => bucket.test(value));
    if (!buckets.length) {
      throw new Error(`${value} is out of bound`);
    }
    buckets.forEach(bucket => bucket.add(value));
    _classPrivateFieldLooseBase(this, _count)[_count]++;
    _classPrivateFieldLooseBase(this, _sum)[_sum] += value;
  }
  get buckets() {
    return _classPrivateFieldLooseBase(this, _buckets)[_buckets];
  }
  get count() {
    return _classPrivateFieldLooseBase(this, _count)[_count];
  }
  reset() {
    _classPrivateFieldLooseBase(this, _buckets)[_buckets].forEach(bucket => bucket.reset());
    _classPrivateFieldLooseBase(this, _count)[_count] = 0;
    _classPrivateFieldLooseBase(this, _sum)[_sum] = 0;
  }
  serialize() {
    return _extends({}, super.serialize(), {
      histogram: {
        buckets: _classPrivateFieldLooseBase(this, _buckets)[_buckets].map(bucket => bucket.serialize()),
        count: _classPrivateFieldLooseBase(this, _count)[_count],
        sum: _classPrivateFieldLooseBase(this, _sum)[_sum]
      }
    });
  }
  get sum() {
    return _classPrivateFieldLooseBase(this, _sum)[_sum];
  }
}
var _less_eq = /*#__PURE__*/_classPrivateFieldLooseKey("less_eq");
var _count2 = /*#__PURE__*/_classPrivateFieldLooseKey("count");
class HistogramBucket {
  constructor(limit) {
    Object.defineProperty(this, _less_eq, {
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, _count2, {
      writable: true,
      value: 0
    });
    _classPrivateFieldLooseBase(this, _less_eq)[_less_eq] = limit;
  }
  add(value) {
    if (!this.test(value)) {
      throw new Error(`${value} is out of bound`);
    }
    _classPrivateFieldLooseBase(this, _count2)[_count2]++;
  }
  reset() {
    _classPrivateFieldLooseBase(this, _count2)[_count2] = 0;
  }
  serialize() {
    return {
      count: _classPrivateFieldLooseBase(this, _count2)[_count2],
      less_eq: _classPrivateFieldLooseBase(this, _less_eq)[_less_eq] === Infinity ? '+Inf' : _classPrivateFieldLooseBase(this, _less_eq)[_less_eq].toString()
    };
  }
  test(value) {
    return value <= _classPrivateFieldLooseBase(this, _less_eq)[_less_eq];
  }
}
//# sourceMappingURL=HistogramMetrics.js.map